// features/inventory/pages/SetupPage.tsx
/**
 * Initial Stock Setup Page — Onboarding wizard with visual feedback.
 * 
 * Enhanced with:
 * - Auto branch detection (fetches first available branch)
 * - Visual progress indicators during template generation
 * - Product count display
 * - Real-time upload progress
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { BulkUploadProgress } from '@/components/bulk-upload/BulkUploadProgress';
import { BulkUploadErrors, BulkUploadErrorDetail } from '@/components/bulk-upload/BulkUploadErrors';
import {
    downloadCountTemplate,
    bulkUploadCount,
    BulkCountResult,
} from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProducts } from '@/features/products/services/productsApi';
import {
    ArrowLeft, Upload, Download, FileSpreadsheet, Warehouse,
    CheckCircle, AlertTriangle, Package, ArrowRight,
    ClipboardList, Sparkles, Store, Loader2, Database,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

type SetupStep = 'loading' | 'welcome' | 'download' | 'upload' | 'processing' | 'complete';

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SetupPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [step, setStep] = useState<SetupStep>('loading');
    const [selectedBranchId, setSelectedBranchId] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadResult, setUploadResult] = useState<BulkCountResult | null>(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [templateDownloading, setTemplateDownloading] = useState(false);

    // Progress state
    const [bulkUploadState, setBulkUploadState] = useState<{
        isOpen: boolean; status: 'processing' | 'completed' | 'failed'; progress: number;
        steps: Array<{ id: string; label: string; icon: React.ElementType; status: 'pending' | 'active' | 'completed' | 'error' }>;
        result: BulkCountResult | null; errorMessage?: string;
    }>({
        isOpen: false, status: 'processing', progress: 0,
        steps: [
            { id: 'validate', label: 'Validating file', icon: FileSpreadsheet, status: 'pending' },
            { id: 'match', label: 'Matching products', icon: Package, status: 'pending' },
            { id: 'update', label: 'Setting stock levels', icon: Warehouse, status: 'pending' },
            { id: 'finalize', label: 'Finalizing', icon: CheckCircle, status: 'pending' },
        ],
        result: null, errorMessage: undefined,
    });

    // Queries — Fetch branches and products on mount
    const { data: branches, isLoading: branchesLoading } = useQuery({
        queryKey: ['branches-setup', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const { data: productsData, isLoading: productsLoading } = useQuery({
        queryKey: ['products-setup', user?.tenantId],
        queryFn: async () => {
            const response = await getProducts(user!.tenantId, { limit: 1 });
            return response?.data || response?.meta?.totalCount || 0;
        },
        enabled: !!user?.tenantId,
    });

    // Auto-select branch and advance from loading state
    useEffect(() => {
        if (!branchesLoading && step === 'loading') {
            const branchesList = (branches || []) as Array<{ id: string; name: string }>;
            if (branchesList.length > 0) {
                // Select the first active branch or the main branch
                const mainBranch = branchesList.find((b: any) => b.isMainBranch) || branchesList[0];
                setSelectedBranchId(mainBranch.id);
            }
            setStep('welcome');
        }
    }, [branches, branchesLoading, step]);

    const branchesList = (branches || []) as Array<{ id: string; name: string; code?: string; isMainBranch?: boolean }>;
    const isSingleStore = branchesList.length <= 1;
    const productCount = typeof productsData === 'number' ? productsData : 0;
    const getBranchName = (id: string) => branchesList.find((b: any) => b.id === id)?.name || 'Warehouse';

    // Mutations
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            setStep('processing');
            setBulkUploadState((prev) => ({
                ...prev, isOpen: true, status: 'processing', progress: 0,
                steps: prev.steps.map((s) => ({ ...s, status: 'pending' as const })),
                result: null, errorMessage: undefined,
            }));

            const updateProgress = (stepId: string, progress: number) => {
                setBulkUploadState((prev) => ({
                    ...prev, progress,
                    steps: prev.steps.map((s) => {
                        if (s.id === stepId) return { ...s, status: 'active' as const };
                        const si = prev.steps.findIndex((ps) => ps.id === s.id);
                        const ai = prev.steps.findIndex((ps) => ps.id === stepId);
                        if (si < ai) return { ...s, status: 'completed' as const };
                        return s;
                    }),
                }));
            };

            updateProgress('validate', 20);
            updateProgress('match', 45);

            // Use selected branch or first available branch
            const effectiveBranchId = selectedBranchId || branchesList[0]?.id || '';
            const result = await bulkUploadCount(user!.tenantId, effectiveBranchId, file);

            updateProgress('update', 75);
            updateProgress('finalize', 95);
            return result;
        },
        onSuccess: (result) => {
            const hasErrors = result.hasErrors || result.errorCount > 0;
            setBulkUploadState((prev) => ({
                ...prev, progress: 100,
                status: hasErrors ? 'failed' : 'completed',
                steps: prev.steps.map((s) => ({
                    ...s,
                    status: s.id === 'finalize' && hasErrors ? ('error' as const) : ('completed' as const),
                })),
                result,
            }));
            setStep('complete');
            setUploadResult(result);
            queryClient.invalidateQueries({ queryKey: ['inventory'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            queryClient.invalidateQueries({ queryKey: ['low-stock-dash'] });
            queryClient.invalidateQueries({ queryKey: ['confirm-status-dash'] });
            addToast({
                type: hasErrors ? 'warning' : 'success',
                title: 'Stock setup completed',
                message: `${result.successCount} products updated.`,
            });
        },
        onError: (err: any) => {
            setStep('upload');
            setBulkUploadState((prev) => ({
                ...prev, progress: 100, status: 'failed',
                errorMessage: err?.message || 'Setup failed.',
                steps: prev.steps.map((s) => ({ ...s, status: s.status === 'active' ? 'error' as const : s.status })),
                result: null,
            }));
            addToast({ type: 'error', title: 'Setup failed', message: err?.message });
        },
    });

    // Handlers
    const handleFileSelect = useCallback((file: File) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
            addToast({ type: 'error', title: 'Invalid file', message: 'Please upload an Excel or CSV file.' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            addToast({ type: 'error', title: 'File too large', message: 'Maximum file size is 10MB.' });
            return;
        }
        setSelectedFile(file);
        setStep('upload');
    }, [addToast]);

    const handleDownloadTemplate = async () => {
        setTemplateDownloading(true);
        try {
            const effectiveBranchId = selectedBranchId || branchesList[0]?.id || '';
            const blob = await downloadCountTemplate(user!.tenantId, effectiveBranchId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'inventory-setup-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
            addToast({ type: 'success', title: 'Template downloaded', message: 'Open the file and fill in stock quantities.' });
        } catch {
            addToast({ type: 'error', title: 'Download failed', message: 'Could not generate template. Try again.' });
        } finally {
            setTemplateDownloading(false);
        }
    };

    // ============================================================
    // LOADING STATE
    // ============================================================

    if (step === 'loading') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
                <div className="text-center">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className="w-20 h-20 rounded-2xl border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"
                    />
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Preparing Your Setup</h2>
                    <p className="text-sm text-slate-500">
                        {branchesLoading ? 'Fetching branches...' : productsLoading ? 'Counting your products...' : 'Almost ready...'}
                    </p>
                </div>
            </div>
        );
    }

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-3xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/inventory')} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <Warehouse className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">Initial Stock Setup</h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(selectedBranchId)} · {productCount > 0 ? `${productCount} products` : 'Loading products...'}
                                </p>
                            </div>
                        </div>
                        {/* Step Indicator */}
                        <div className="flex items-center gap-1">
                            {[1, 2, 3].map((s) => (
                                <div key={s} className="flex items-center gap-1">
                                    <div className={clsx(
                                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                                        step === 'welcome' && s === 1 || step === 'download' && s <= 1 ||
                                            step === 'upload' && s <= 2 || step === 'processing' && s <= 3 || step === 'complete'
                                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                                            : 'bg-slate-200 text-slate-500'
                                    )}>
                                        {step === 'download' || step === 'upload' || step === 'processing' || step === 'complete' ? (
                                            s <= (step === 'download' ? 1 : step === 'upload' ? 2 : 3) ? <CheckCircle className="w-4 h-4" /> : s
                                        ) : s}
                                    </div>
                                    {s < 3 && <div className={clsx('w-6 h-0.5 rounded', step !== 'welcome' && s < (step === 'download' ? 1 : step === 'upload' ? 2 : 3) ? 'bg-blue-400' : 'bg-slate-200')} />}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8 space-y-6">
                {/* STEP 1: Welcome */}
                {step === 'welcome' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card>
                            <CardContent className="p-8 text-center">
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                    className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-500/30">
                                    <Warehouse className="w-10 h-10 text-white" />
                                </motion.div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Set Up Your Initial Stock</h2>
                                <p className="text-sm text-slate-500 max-w-md mx-auto">
                                    We've detected <span className="font-bold text-blue-600">{productCount} products</span> in your catalog.
                                    Download the pre-filled template, enter stock quantities, and upload.
                                </p>

                                {/* Branch selector if multi-store */}
                                {!isSingleStore && branchesList.length > 0 && (
                                    <div className="mt-6 max-w-sm mx-auto">
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            <Store className="w-3.5 h-3.5 inline mr-1" /> Branch for Initial Stock
                                        </label>
                                        <select
                                            value={selectedBranchId}
                                            onChange={(e) => setSelectedBranchId(e.target.value)}
                                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                        >
                                            {branchesList.map((b: any) => (
                                                <option key={b.id} value={b.id}>{b.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="mt-8 flex items-center justify-center gap-3">
                                    <button onClick={() => setStep('upload')}
                                        className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-semibold text-sm" type="button">
                                        Skip to Upload
                                    </button>
                                    <button onClick={() => setStep('download')}
                                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 transition-all font-semibold shadow-lg text-sm" type="button">
                                        Get Started <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 2: Download Template */}
                {step === 'download' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card>
                            <CardContent className="p-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100">
                                        <Download className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold">Download Template</h2>
                                        <p className="text-sm text-slate-500">
                                            Pre-filled with {productCount} products for {getBranchName(selectedBranchId)}
                                        </p>
                                    </div>
                                </div>

                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6 flex items-start gap-3">
                                    <ClipboardList className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-amber-700">Only fill the "Counted Quantity" column</p>
                                        <p className="text-xs text-amber-600 mt-0.5">All other columns are for reference. Do not modify product names, SKUs, or barcodes.</p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleDownloadTemplate}
                                    disabled={templateDownloading}
                                    className="flex items-center gap-2 w-full justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold shadow-lg disabled:opacity-70"
                                    type="button"
                                >
                                    {templateDownloading ? (
                                        <><Loader2 className="w-5 h-5 animate-spin" /> Generating template...</>
                                    ) : (
                                        <><Download className="w-5 h-5" /> Download Pre-Filled Template</>
                                    )}
                                </button>

                                <div className="mt-4 flex justify-end">
                                    <button onClick={() => setStep('upload')}
                                        className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-emerald-200 text-emerald-700 rounded-xl font-semibold text-sm" type="button">
                                        I've filled it <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 3: Upload */}
                {step === 'upload' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card>
                            <CardContent className="p-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                        <Upload className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold">Upload Filled Template</h2>
                                        <p className="text-sm text-slate-500">
                                            {selectedFile ? selectedFile.name : 'Excel or CSV with stock quantities'}
                                            {' · '}{getBranchName(selectedBranchId)}
                                        </p>
                                    </div>
                                </div>

                                <div
                                    onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFileSelect(f); }}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    className={clsx(
                                        'border-2 border-dashed rounded-2xl p-16 text-center transition-all cursor-pointer',
                                        isDragging ? 'border-blue-400 bg-blue-50/50' : selectedFile ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-300 hover:border-blue-400'
                                    )}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                                        onChange={(e) => { const f = e.target.files?.[0]; if (f) { handleFileSelect(f); e.target.value = ''; } }} />
                                    {selectedFile ? (
                                        <div>
                                            <div className="w-20 h-20 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                                <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                                            </div>
                                            <h3 className="text-lg font-bold">{selectedFile.name}</h3>
                                            <p className="text-sm text-slate-400">{(selectedFile.size / 1024).toFixed(1)} KB · Click to change</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="w-20 h-20 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                                                <Upload className="w-10 h-10 text-blue-600" />
                                            </div>
                                            <h3 className="text-lg font-bold">{isDragging ? 'Drop here' : 'Click or drag to upload'}</h3>
                                            <p className="text-sm text-slate-400">Excel (.xlsx, .xls) or CSV · Max 10MB</p>
                                        </div>
                                    )}
                                </div>

                                {selectedFile && (
                                    <div className="mt-6 flex items-center gap-3 justify-center">
                                        <button onClick={() => setSelectedFile(null)}
                                            className="px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm" type="button">Remove</button>
                                        <button onClick={() => uploadMutation.mutate(selectedFile)} disabled={uploadMutation.isPending}
                                            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg disabled:opacity-50" type="button">
                                            {uploadMutation.isPending ? <Spinner size="xs" variant="white" /> : <Sparkles className="w-4 h-4" />}
                                            Process Stock Setup
                                        </button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* STEP 4: Complete */}
                {step === 'complete' && uploadResult && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <Card className={clsx('border-2', uploadResult.hasErrors ? 'border-amber-200' : 'border-emerald-200')}>
                            <CardContent className="p-8 text-center">
                                <div className={clsx('w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6', uploadResult.hasErrors ? 'bg-amber-100' : 'bg-emerald-100')}>
                                    {uploadResult.hasErrors ? <AlertTriangle className="w-10 h-10 text-amber-600" /> : <Sparkles className="w-10 h-10 text-emerald-600" />}
                                </div>
                                <h2 className="text-2xl font-bold">{uploadResult.hasErrors ? 'Setup Complete with Issues' : 'Stock Setup Complete!'}</h2>
                                <p className="text-sm text-slate-500 mt-2">
                                    {uploadResult.successCount} products updated · {uploadResult.errorCount} errors · {uploadResult.skippedCount || 0} skipped
                                </p>
                            </CardContent>
                        </Card>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-2xl border p-5 text-center">
                                <p className="text-xs text-slate-400 uppercase">Total</p>
                                <p className="text-3xl font-bold">{uploadResult.totalRows}</p>
                            </div>
                            <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5 text-center">
                                <p className="text-xs text-emerald-600 uppercase">Updated</p>
                                <p className="text-3xl font-bold text-emerald-700">{uploadResult.successCount}</p>
                            </div>
                        </div>
                        {uploadResult.hasErrors && (
                            <div className="flex justify-center">
                                <button onClick={() => setShowErrorDetails(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm font-semibold" type="button">
                                    <AlertTriangle className="w-4 h-4" /> View Errors ({uploadResult.errorCount})
                                </button>
                            </div>
                        )}
                        <div className="flex justify-center gap-3">
                            <button onClick={() => { setStep('welcome'); setSelectedFile(null); setUploadResult(null); }}
                                className="flex items-center gap-2 px-5 py-3 bg-white border rounded-xl text-sm font-medium shadow-sm" type="button">
                                <Upload className="w-4 h-4" /> Upload Another
                            </button>
                            <button onClick={() => navigate('/inventory')}
                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg text-sm" type="button">
                                Go to Inventory
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Progress & Error Modals */}
            <BulkUploadProgress
                isOpen={bulkUploadState.isOpen && !showErrorDetails}
                onClose={() => { if (bulkUploadState.status !== 'processing') { setBulkUploadState(prev => ({ ...prev, isOpen: false })); if (bulkUploadState.result?.hasErrors) setShowErrorDetails(true); } }}
                steps={bulkUploadState.steps} progress={bulkUploadState.progress} status={bulkUploadState.status}
                stats={{ totalRows: bulkUploadState.result?.totalRows || 0, successCount: bulkUploadState.result?.successCount || 0, errorCount: bulkUploadState.result?.errorCount || 0, categoriesCreated: 0 }}
                errorMessage={bulkUploadState.errorMessage}
            />
            <BulkUploadErrors
                isOpen={showErrorDetails} onClose={() => setShowErrorDetails(false)}
                errors={(uploadResult?.errors || []) as BulkUploadErrorDetail[]}
                totalRows={uploadResult?.totalRows || 0} successCount={uploadResult?.successCount || 0} errorCount={uploadResult?.errorCount || 0}
            />
        </div>
    );
}